<template>
  <Modal
    v-model:visible="isVisible"
    class="purchase-cargo-insurance-modal"
    :header="t('cargoInsurance.cargoInsuranceQuote')"
  >
    <Message
      v-for="(err, i) of cargoInsuranceErrors"
      :key="i"
      :severity="err.severity"
      class="rygen-message"
    >
      <div class="font-bold">
        {{ err.message }}
      </div>
      <div class="flex flex-gap-1 text-sm">
        <span v-if="err.errorCode">
          {{ t('common.codes') }}: {{ err.errorCode }}
        </span>
        <span>
          {{
            t('cargoInsurance.errors.source', {
              sourceName: t('cargoInsurance.errors.provider'),
            })
          }}
        </span>
      </div>
    </Message>

    <PurchaseCargoInsuranceAdditionalInformation
      v-if="!isLoading && state === 'building_quote_request'"
      v-model:quote-request="cargoInsuranceQuoteRequest"
      :shipment="shipment"
    />

    <PurchaseCargoInsuranceQuoteDetail
      v-if="!isLoading && cargoInsuranceQuote.data && state === 'quote_review'"
      v-model:commodity-exclusion-acknowledgements="
        commodityExclusionAcknowledgements
      "
      v-model:terms-and-conditions-acknowledgement="
        termsAndConditionsAcknowledgement
      "
      :cargo-insurance-quote="cargoInsuranceQuote.data"
    />

    <PurchaseCargoInsuranceCertificateDetail
      v-if="
        !isLoading &&
        cargoInsuranceCertificate.data &&
        state === 'certificate_review'
      "
      :cargo-insurance-certificate="cargoInsuranceCertificate.data"
    />

    <PurchaseCargoInsuranceLoadingSkeleton v-if="isLoading" />

    <template #footer>
      <RygenButton
        v-if="state !== 'certificate_review'"
        mode="tertiary"
        :label="t('common.cancel')"
        @click="() => (isVisible = false)"
      />
      <RygenButton
        :label="primaryAction.label"
        :disabled="primaryAction.disabled"
        mode="primary"
        @click="primaryAction.action"
      />
    </template>
  </Modal>
</template>

<script setup lang="ts">
import axios from 'axios';
import type { Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast, useUid, useValidations } from '@rygen/rygen-ui/composables';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import { Shipment } from '@/model';
import {
  CargoInsuranceBadRequestResponse,
  CargoInsuranceCertificate,
  CargoInsuranceProviderError,
  CargoInsuranceQuote,
  CargoInsuranceQuoteRequest,
} from '@/model/cargoInsurance';

import PurchaseCargoInsuranceAdditionalInformation from '@/components/cargoInsurance/PurchaseCargoInsuranceAdditionalInformation.vue';
import PurchaseCargoInsuranceCertificateDetail from '@/components/cargoInsurance/PurchaseCargoInsuranceCertificateDetail.vue';
import PurchaseCargoInsuranceLoadingSkeleton from '@/components/cargoInsurance/PurchaseCargoInsuranceLoadingSkeleton.vue';
import PurchaseCargoInsuranceQuoteDetail from '@/components/cargoInsurance/PurchaseCargoInsuranceQuoteDetail.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import Message from 'primevue/message';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  shipment: {
    type: Shipment,
    required: true,
  },
});
const { shipment } = toRefs(props);
const emit = defineEmits<{
  (
    event: 'purchased',
    cargoInsuranceCertificate: CargoInsuranceCertificate,
  ): void;
}>();

const cargoInsuranceQuote: Ref<Loadable<CargoInsuranceQuote>> = ref({
  loading: false,
});

const { t } = useI18n();
const toast = useToast();
const { uid } = useUid('purchase-cargo-insurance-modal');
const { validate } = useValidations(cargoInsuranceQuote, {}, uid);

const cargoInsuranceQuoteRequest = ref(new CargoInsuranceQuoteRequest());
const commodityExclusionAcknowledgements = ref<Record<string, boolean>>({});
const termsAndConditionsAcknowledgement = ref(false);
const cargoInsuranceCertificate: Ref<Loadable<CargoInsuranceCertificate>> = ref(
  { loading: false },
);

const extractProviderErrors = (error: any): CargoInsuranceProviderError[] => {
  if (!axios.isAxiosError(error)) {
    return [];
  }

  const errorData = error.response?.data as any;
  const cargoInsuranceBadRequestResponse = new CargoInsuranceBadRequestResponse(
    errorData?.errorDetails,
  );
  return cargoInsuranceBadRequestResponse.allMessages.filter((x) => x?.message);
};

const cargoInsuranceErrors: Ref<CargoInsuranceProviderError[]> = computed(
  () => {
    return [
      ...extractProviderErrors(cargoInsuranceQuote.value.error),
      ...extractProviderErrors(cargoInsuranceCertificate.value.error),
    ];
  },
);

const userCompletedAllAcknowledgements = computed(() => {
  const allCommodityExclusionAcknowledged = Object.values(
    commodityExclusionAcknowledgements.value,
  ).every((acknowledged) => acknowledged);
  return (
    allCommodityExclusionAcknowledged && termsAndConditionsAcknowledgement.value
  );
});

type PurchaseCargoInsuranceModalState =
  'building_quote_request' | 'quote_review' | 'certificate_review';
const state: Ref<PurchaseCargoInsuranceModalState> = computed(() => {
  if (cargoInsuranceCertificate.value.data) {
    return 'certificate_review';
  }

  if (cargoInsuranceQuote.value.data) {
    return 'quote_review';
  }

  return 'building_quote_request';
});

const isLoading = computed(
  () =>
    cargoInsuranceQuote.value.loading ||
    cargoInsuranceCertificate.value.loading,
);

const primaryAction: Ref<{
  label: string;
  disabled: boolean;
  action: () => Promise<void> | void;
}> = computed(() => {
  if (state.value === 'building_quote_request') {
    return {
      label: t('cargoInsurance.getQuote'),
      disabled: isLoading.value /* && isValid */,
      action: fetchCargoInsuranceQuote,
    };
  }

  if (state.value === 'quote_review') {
    return {
      label: t('cargoInsurance.purchase'),
      disabled: isLoading.value || !userCompletedAllAcknowledgements.value,
      action: purchaseCargoInsurance,
    };
  }

  if (state.value === 'certificate_review') {
    return {
      label: t('common.done'),
      disabled: isLoading.value,
      action: () => {
        isVisible.value = false;
      },
    };
  }

  return {
    label: t('cargoInsurance.cargoInsuranceQuote'),
    disabled: true,
    action: () => {},
  };
});

const resetState = () => {
  cargoInsuranceQuote.value = { loading: false };
  cargoInsuranceQuoteRequest.value = new CargoInsuranceQuoteRequest();
  commodityExclusionAcknowledgements.value = {};
  termsAndConditionsAcknowledgement.value = false;
  cargoInsuranceCertificate.value = { loading: false };
};

const fetchCargoInsuranceQuote = async () => {
  const isValid = await validate();
  if (!isValid) {
    return;
  }
  const shipmentId = shipment.value.id;
  if (!shipmentId) {
    throw new Error('Shipment ID is required');
  }

  cargoInsuranceQuote.value = { loading: true };
  try {
    cargoInsuranceQuote.value.data = await api.shipmentCargoInsurance.getQuote(
      shipmentId,
      cargoInsuranceQuoteRequest.value,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    cargoInsuranceQuote.value.error = e as Error;
    toast.addError({
      detail: t('cargoInsurance.errors.fetchingQuote'),
      error: e,
    });
  } finally {
    cargoInsuranceQuote.value.loading = false;
  }
};

const purchaseCargoInsurance = async () => {
  const shipmentId = shipment.value.id;
  if (!shipmentId) {
    throw new Error('Shipment ID is required');
  }

  const corsairShipmentCargoInsuranceId =
    cargoInsuranceQuote.value.data?.corsairShipmentCargoInsuranceId;
  if (!corsairShipmentCargoInsuranceId) {
    throw new Error('Cargo insurance ID is required');
  }

  cargoInsuranceCertificate.value = { loading: true };
  try {
    cargoInsuranceCertificate.value.data =
      await api.shipmentCargoInsurance.purchase(
        shipmentId,
        corsairShipmentCargoInsuranceId,
        commodityExclusionAcknowledgements.value,
        termsAndConditionsAcknowledgement.value,
      );
    toast.addSuccess({ detail: t('cargoInsurance.success.purchase') });
    emit('purchased', cargoInsuranceCertificate.value.data);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    cargoInsuranceCertificate.value.error = e as Error;
    toast.addError({
      detail: t('cargoInsurance.errors.purchasingQuote'),
      error: e,
    });
  } finally {
    cargoInsuranceCertificate.value.loading = false;
  }
};

watch(
  () => cargoInsuranceQuote.value.data,
  (newVal) => {
    const commodityExclusions = newVal?.commodityExclusions || [];
    commodityExclusionAcknowledgements.value = commodityExclusions.reduce(
      (acc, exclusion) => {
        acc[exclusion.id] = false;
        return acc;
      },
      {} as Record<string, boolean>,
    );
    termsAndConditionsAcknowledgement.value = false;
  },
  { deep: true, immediate: true },
);

watch(isVisible, () => {
  resetState();
});
</script>

<style>
.rygen-modal.p-dialog.purchase-cargo-insurance-modal {
  --total-modal-height: 90vh;
  --approx-header-height: 75px;
  --approx-footer-height: 75px;

  width: 35vw;
  max-width: 1000px;
  max-height: 90vh;

  .p-dialog-content {
    padding-top: 0;

    .modal-content.scrollable {
      max-height: calc(
        var(--total-modal-height) - var(--approx-header-height) -
          var(--approx-footer-height)
      );
      display: flex;
      flex-direction: column;
      gap: var(--sp-3);

      .p-fieldset .p-fieldset-legend {
        padding: var(--sp-2);
        border: initial;
        background: initial;
      }
    }
  }
}
</style>
